import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { inr, inrExact, Sheet, Stepper, fmtDateLong } from '../design/atoms';
import { featureChips, durationLabel } from '../planLabels';
import CoachStep, { CoachChoice } from '../components/CoachStep';
import WeeklySlots, { WeeklyTime, timeLabel } from '../components/WeeklySlots';

/* The member store: plans, session packs, services and products the gym sells
   online. Prices shown here are the catalogue's; the server re-prices the cart
   at review (GST split, membership rules) and again at checkout. Paying opens
   the same hosted pay page a WhatsApp link does; the sale is created when the
   payment lands. */

interface Include { classId: string; name: string; bookableType: string; quotaType: string; quota: number | null; period: string | null }
/** The offering lets the member pick who trains them. */
interface CoachSummary { required: boolean; allowAny: boolean; fromPrice: number; coachCount: number }
/** The offering holds weekly times with the coach (fixed = must choose; member_choice = may). */
interface SlotStep { scheduling: 'fixed_recurring' | 'member_choice'; min: number; max: number; durationMin: number | null }
interface Catalogue {
  plans: Array<{ id: string; name: string; description: string | null; price: number; gstPercent: number; durationMonths: number | null; features: Record<string, any> | null; includes: Include[]; coach: CoachSummary | null; slots: SlotStep | null }>;
  packs: Array<{ id: string; name: string; description: string | null; price: number; gstPercent: number; sessions: number; period: 'week' | 'month' | null; validityDays: number | null; forClass: { id: string; name: string } | null; requiresMembership: boolean; coach: CoachSummary | null; slots: SlotStep | null }>;
  services: Array<{ id: string; name: string; description: string | null; price: number; gstPercent: number }>;
  products: Array<{ id: string; name: string; price: number; gstPercent: number; inStock: boolean }>;
  membership: { current: { planName: string; endDate: string | null } | null; earliestStart: string };
}
interface Quote {
  subtotal: number; discountAmount: number; taxAmount: number; cgst: number; sgst: number; igst: number; total: number;
  documentType: 'tax_invoice' | 'bill_of_supply';
  lines: Array<{ name: string; quantity: number; price: number; taxableValue: number; gstPercent: number; tax: number }>;
}
type Kind = 'plan' | 'pack' | 'service' | 'product';
type CartLine = { kind: Kind; qty: number; coach?: CoachChoice; slots?: { times: WeeklyTime[]; branchId: string } };
type Cart = Record<string, CartLine>; // key: `${kind}:${id}`

const includeLabel = (i: Include) =>
  i.quotaType === 'unlimited' ? `${i.name} · unlimited` : `${i.name} · ${i.quota}/${i.period === 'week' ? 'week' : 'month'}`;

export default function MemberStorePage({ gymId, gymName }: { gymId: string; gymName: string }) {
  const { token } = useMemberAuth();
  const navigate = useNavigate();
  const [cat, setCat] = useState<Catalogue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<Cart>({});
  const [reviewing, setReviewing] = useState(false);
  const [coachFor, setCoachFor] = useState<{ kind: 'plan' | 'pack'; id: string } | null>(null);
  // The weekly-slots step follows the coach step for offerings that hold times.
  const [slotsFor, setSlotsFor] = useState<{ kind: 'plan' | 'pack'; id: string; coach: CoachChoice; startDate?: string } | null>(null);

  // Arriving from a coach's profile ("Train with Priya"): that offering, with
  // that coach, is already in the cart.
  const location = useLocation();
  useEffect(() => {
    const add = (location.state as any)?.add;
    if (add?.kind && add?.id) {
      setCart({ [`${add.kind}:${add.id}`]: { kind: add.kind, qty: 1, ...(add.coach && { coach: add.coach }) } });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    if (!token) return;
    setError(null);
    mfetch(token, `/member/gyms/${gymId}/store`)
      .then(r => setCat(r.data))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load the store'));
  }, [token, gymId]);
  useEffect(load, [load]);

  const priceOf = useCallback((key: string) => {
    if (!cat) return 0;
    const chosenCoach = cart[key]?.coach;
    if (chosenCoach) return chosenCoach.price; // the coach's price for this offering
    const [kind, id] = key.split(':');
    const list = kind === 'plan' ? cat.plans : kind === 'pack' ? cat.packs : kind === 'service' ? cat.services : cat.products;
    return (list as Array<{ id: string; price: number }>).find(x => x.id === id)?.price ?? 0;
  }, [cat, cart]);

  const items = Object.entries(cart).filter(([, v]) => v.qty > 0);
  const count = items.reduce((n, [, v]) => n + v.qty, 0);
  const estimate = items.reduce((sum, [k, v]) => sum + priceOf(k) * v.qty, 0);
  const planKey = items.find(([k]) => k.startsWith('plan:'))?.[0];

  const setQty = (kind: Kind, id: string, qty: number, coach?: CoachChoice, slots?: CartLine['slots']) =>
    setCart(c => {
      const next = { ...c };
      if (kind === 'plan') for (const k of Object.keys(next)) if (k.startsWith('plan:')) delete next[k]; // one plan per order
      next[`${kind}:${id}`] = { kind, qty, ...(coach && { coach }), ...(slots && { slots }) };
      return next;
    });
  const slotStepOf = (kind: 'plan' | 'pack', id: string) =>
    (kind === 'plan' ? cat?.plans.find(p => p.id === id)?.slots : cat?.packs.find(p => p.id === id)?.slots) ?? null;
  // After the coach: offerings that hold weekly times go on to choose them.
  const coachChosen = (kind: 'plan' | 'pack', id: string, coach: CoachChoice) => {
    setCoachFor(null);
    if (slotStepOf(kind, id) && coach.trainerId) return setSlotsFor({ kind, id, coach });
    setQty(kind, id, 1, coach);
  };
  // Offerings with a coach step open it instead of going straight in the cart.
  const choose = (kind: 'plan' | 'pack', id: string, coach: CoachSummary | null, inCart: boolean) => {
    if (inCart) return setQty(kind, id, 0);
    if (coach) return setCoachFor({ kind, id });
    setQty(kind, id, 1);
  };
  const qtyOf = (kind: Kind, id: string) => cart[`${kind}:${id}`]?.qty ?? 0;

  if (error) {
    return (
      <div className="mpage">
        <Header onBack={() => navigate(-1)} gymName={gymName} />
        <div className="card flat" style={{ padding: 16 }}>
          <div className="col" style={{ gap: 8 }}>
            <div className="mid wrap">{error}</div>
            <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={load}>Retry</button>
          </div>
        </div>
      </div>
    );
  }
  if (!cat) {
    return (
      <div className="mpage">
        <Header onBack={() => navigate(-1)} gymName={gymName} />
        {[0, 1, 2].map(i => <div key={i} className="skel" style={{ height: 130, borderRadius: 'var(--r-m)' }} />)}
      </div>
    );
  }

  const empty = !cat.plans.length && !cat.packs.length && !cat.services.length && !cat.products.length;

  return (
    <div className="mpage" style={count ? { paddingBottom: 'calc(var(--tabbar-clearance) + 84px)' } : undefined}>
      <Header onBack={() => navigate(-1)} gymName={gymName} />

      {cat.membership.current && (
        <div className="card tint" style={{ padding: 14 }}>
          <div className="sub wrap">
            Your plan <b>{cat.membership.current.planName}</b>
            {cat.membership.current.endDate ? ` runs until ${fmtDateLong(cat.membership.current.endDate)}` : ' is ongoing'}.
            {' '}A new plan starts {fmtDateLong(cat.membership.earliestStart)}.
          </div>
        </div>
      )}

      {empty && (
        <div className="card flat" style={{ padding: 16 }}>
          <div className="mid">Nothing is on sale online yet — ask at the front desk.</div>
        </div>
      )}

      {cat.plans.length > 0 && <div className="lbl">Plans</div>}
      {cat.plans.map(p => {
        const chosen = qtyOf('plan', p.id) > 0;
        const coachPick = cart[`plan:${p.id}`]?.coach;
        const duration = durationLabel(p.durationMonths);
        const chips = [...p.includes.map(includeLabel), ...featureChips(p.features)];
        return (
          <div key={p.id} className={`card${chosen ? ' tint3' : ''}`} style={{ padding: 16 }}>
            <div className="col" style={{ gap: 10 }}>
              <div className="between" style={{ alignItems: 'flex-start', gap: 12 }}>
                <div className="col" style={{ gap: 2, minWidth: 0 }}>
                  <div className="dp dp-m trunc">{p.name}</div>
                  {duration && <div className="tiny">{duration}</div>}
                </div>
                <PriceTag base={p.price} coach={p.coach} picked={coachPick} large />
              </div>
              {p.description && <div className="sub wrap">{p.description}</div>}
              {chips.length > 0 && (
                <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                  {chips.map(c => <span key={c} className="tag">{c}</span>)}
                </div>
              )}
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <button className={`btn ${chosen ? '' : 'gold'} sm`} onClick={() => choose('plan', p.id, p.coach, chosen)}>
                  {chosen ? <><Icon n="check" s={15} /> Chosen</> : p.coach ? 'Choose your coach' : 'Choose plan'}
                </button>
                {chosen && p.coach && <CoachLine pick={coachPick} onChange={() => setCoachFor({ kind: 'plan', id: p.id })} />}
              </div>
              {chosen && <SlotsLine line={cart[`plan:${p.id}`]} onChange={() => coachPick && setSlotsFor({ kind: 'plan', id: p.id, coach: coachPick })} />}
            </div>
          </div>
        );
      })}

      {cat.packs.length > 0 && <div className="lbl">Session packs</div>}
      {cat.packs.map(pk => {
        const added = qtyOf('pack', pk.id) > 0;
        const coachPick = cart[`pack:${pk.id}`]?.coach;
        return (
          <div key={pk.id} className={`card${added ? ' tint3' : ''}`} style={{ padding: 16 }}>
            <div className="between" style={{ alignItems: 'center', gap: 12 }}>
              <div className="col" style={{ gap: 2, minWidth: 0 }}>
                <div className="mid trunc">{pk.name}</div>
                <div className="tiny trunc">
                  {pk.period ? `${pk.sessions} a ${pk.period}` : `${pk.sessions} sessions`}{pk.forClass ? ` · ${pk.forClass.name}` : ''}{pk.validityDays ? ` · ${pk.period ? '' : 'valid '}${pk.validityDays} days` : ''}
                </div>
                {pk.requiresMembership && <div className="tiny">For members with an active plan</div>}
                {added && pk.coach && <CoachLine pick={coachPick} onChange={() => setCoachFor({ kind: 'pack', id: pk.id })} />}
                {added && <SlotsLine line={cart[`pack:${pk.id}`]} onChange={() => coachPick && setSlotsFor({ kind: 'pack', id: pk.id, coach: coachPick })} />}
              </div>
              <div className="col" style={{ gap: 6, alignItems: 'flex-end' }}>
                <PriceTag base={pk.price} coach={pk.coach} picked={coachPick} />
                <button className={`btn ${added ? '' : 'ghost'} sm`} onClick={() => choose('pack', pk.id, pk.coach, added)}>
                  {added ? 'Added' : pk.coach ? 'Choose coach' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {(cat.services.length > 0 || cat.products.length > 0) && <div className="lbl">Add-ons</div>}
      {[...cat.services.map(s => ({ ...s, kind: 'service' as const, inStock: true })), ...cat.products.map(p => ({ ...p, kind: 'product' as const, description: null }))].map(it => (
        <div key={`${it.kind}:${it.id}`} className="card" style={{ padding: 14 }}>
          <div className="between" style={{ alignItems: 'center', gap: 12 }}>
            <div className="col" style={{ gap: 2, minWidth: 0 }}>
              <div className="mid trunc">{it.name}</div>
              <div className="tiny">{it.inStock ? inr(it.price) : 'Out of stock'}</div>
            </div>
            {it.inStock && <Stepper value={qtyOf(it.kind, it.id)} step={1} onChange={v => setQty(it.kind, it.id, Math.min(10, v))} />}
          </div>
        </div>
      ))}

      {count > 0 && (
        <div style={{ position: 'fixed', left: 16, right: 16, bottom: 'calc(var(--tabbar-clearance) + 12px)', maxWidth: 448, margin: '0 auto', zIndex: 30 }}>
          <button className="btn gold block" onClick={() => setReviewing(true)}>
            {count} item{count > 1 ? 's' : ''} · {inr(estimate)} <span style={{ opacity: 0.6 }}>+ GST</span> · Review <Icon n="right" s={17} />
          </button>
        </div>
      )}

      {coachFor && token && (
        <CoachStep
          token={token}
          gymId={gymId}
          kind={coachFor.kind}
          id={coachFor.id}
          current={cart[`${coachFor.kind}:${coachFor.id}`]?.coach ?? null}
          onClose={() => setCoachFor(null)}
          onChoose={choice => coachChosen(coachFor.kind, coachFor.id, choice)}
        />
      )}

      {slotsFor && token && (() => {
        const step = slotStepOf(slotsFor.kind, slotsFor.id);
        const start = slotsFor.kind === 'plan' ? `&startDate=${slotsFor.startDate ?? cat.membership.earliestStart}` : '';
        return (
          <WeeklySlots
            token={token}
            url={`/member/gyms/${gymId}/store/${slotsFor.kind}/${slotsFor.id}/slots?trainerId=${slotsFor.coach.trainerId}${start}`}
            initial={cart[`${slotsFor.kind}:${slotsFor.id}`]?.slots?.times}
            onClose={() => setSlotsFor(null)}
            onChoose={choice => { setQty(slotsFor.kind, slotsFor.id, 1, slotsFor.coach, { times: choice.times, branchId: choice.branchId }); setSlotsFor(null); }}
            onSkip={step?.scheduling === 'member_choice' ? () => { setQty(slotsFor.kind, slotsFor.id, 1, slotsFor.coach); setSlotsFor(null); } : undefined}
          />
        );
      })()}

      {reviewing && token && (
        <ReviewSheet
          token={token}
          gymId={gymId}
          lines={items.map(([k, v]) => ({
            kind: v.kind, id: k.split(':')[1], quantity: v.qty,
            ...(v.coach && { trainerId: v.coach.trainerId }),
            ...(v.slots && { slots: v.slots.times, branchId: v.slots.branchId }),
          }))}
          hasPlan={!!planKey}
          earliestStart={cat.membership.earliestStart}
          onClose={() => setReviewing(false)}
          onChangeTimes={startDate => {
            // A clash found at review (the time was taken, or the start date moved): back to the grid.
            const hit = items.find(([, v]) => v.slots && v.coach);
            setReviewing(false);
            if (hit) setSlotsFor({ kind: hit[1].kind as 'plan' | 'pack', id: hit[0].split(':')[1], coach: hit[1].coach!, startDate });
          }}
        />
      )}
    </div>
  );
}

function Header({ onBack, gymName }: { onBack: () => void; gymName: string }) {
  return (
    <div className="row" style={{ gap: 10 }}>
      <button className="icon-btn" onClick={onBack} aria-label="Back"><Icon n="left" s={19} /></button>
      <div className="col" style={{ gap: 1, minWidth: 0 }}>
        <div className="dp dp-l">Store</div>
        <div className="sub trunc">{gymName}</div>
      </div>
    </div>
  );
}

/** Price on an offering card: the chosen coach's, or "from ₹X" across coaches. */
function PriceTag({ base, coach, picked, large }: { base: number; coach: CoachSummary | null; picked?: CoachChoice; large?: boolean }) {
  const style = large ? { fontSize: 22 } : undefined;
  if (picked) return <div className="num" style={style}>{inr(picked.price)}</div>;
  if (coach && coach.fromPrice !== base) return <div className="num nw" style={style}><span className="tiny">from </span>{inr(coach.fromPrice)}</div>;
  return <div className="num" style={style}>{inr(base)}</div>;
}

/** The weekly times held with the coach, on a chosen offering. */
function SlotsLine({ line, onChange }: { line?: CartLine; onChange: () => void }) {
  if (!line?.slots) return null;
  return (
    <button className="row" style={{ gap: 6, flexWrap: 'wrap', minHeight: 44 }} onClick={onChange}>
      <Icon n="cal" s={14} />
      <span className="tiny">Every {line.slots.times.map(timeLabel).join(' · ')}</span>
      <span className="tiny" style={{ color: 'var(--gold-2)', fontWeight: 700 }}>Change</span>
    </button>
  );
}

function CoachLine({ pick, onChange }: { pick?: CoachChoice; onChange: () => void }) {
  return (
    <button className="row" style={{ gap: 6 }} onClick={onChange}>
      <span className="tiny trunc">{pick?.trainerName ? `with ${pick.trainerName}` : 'Any available coach'}</span>
      <span className="tiny" style={{ color: 'var(--gold-2)', fontWeight: 700 }}>Change</span>
    </button>
  );
}

function ReviewSheet({ token, gymId, lines, hasPlan, earliestStart, onClose, onChangeTimes }: {
  token: string; gymId: string;
  lines: Array<{ kind: Kind; id: string; quantity: number; trainerId?: string | null; slots?: WeeklyTime[]; branchId?: string }>;
  hasPlan: boolean; earliestStart: string; onClose: () => void;
  onChangeTimes: (startDate: string) => void;
}) {
  const [startDate, setStartDate] = useState(earliestStart);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [slotClash, setSlotClash] = useState(false);
  const [paying, setPaying] = useState(false);

  const body = useMemo(() => ({
    gymId,
    lines: lines.map(l => (l.kind === 'plan' ? { ...l, startDate } : l)),
  }), [gymId, lines, startDate]);

  useEffect(() => {
    let live = true;
    setQuote(null);
    setProblem(null);
    setSlotClash(false);
    mfetch(token, '/member/checkout/quote', { method: 'POST', body })
      .then(r => { if (live) setQuote(r.data); })
      .catch(e => {
        if (!live) return;
        setProblem(e instanceof MemberApiError ? e.message : 'Could not price your order');
        setSlotClash(e instanceof MemberApiError && e.code === 'SLOT_CONFLICT');
      });
    return () => { live = false; };
  }, [token, body]);

  const pay = async () => {
    setPaying(true);
    try {
      const r = await mfetch(token, '/member/checkout', { method: 'POST', body: { ...body, channel: 'web' } });
      window.location.assign(r.data.url);
    } catch (e) {
      setProblem(e instanceof MemberApiError ? e.message : 'Could not start the payment');
      setSlotClash(e instanceof MemberApiError && e.code === 'SLOT_CONFLICT');
      setPaying(false);
    }
  };

  return (
    <Sheet onClose={onClose}>
      <div className="dp dp-m">Review your order</div>

      {hasPlan && (
        <label className="col" style={{ gap: 6 }}>
          <span className="lbl">Plan starts</span>
          <input className="minput" type="date" value={startDate} min={earliestStart} onChange={e => setStartDate(e.target.value || earliestStart)} />
          <span className="tiny wrap">If you pay after this date, your plan starts the day you pay — you never lose days.</span>
        </label>
      )}

      {!quote && !problem && <div className="skel" style={{ height: 120, borderRadius: 'var(--r-s)' }} />}
      {problem && (
        <div className="card flat" style={{ padding: 12 }}>
          <div className="col" style={{ gap: 8 }}>
            <div className="sub wrap">{problem}</div>
            {slotClash && <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => onChangeTimes(startDate)}>Choose other times</button>}
          </div>
        </div>
      )}

      {quote && (
        <div className="col" style={{ gap: 6 }}>
          {quote.lines.map((l, i) => (
            <div key={i} className="between" style={{ gap: 12 }}>
              <span className="sub trunc">{l.quantity > 1 ? `${l.quantity} × ` : ''}{l.name}</span>
              <span className="num sub">{inrExact(l.taxableValue)}</span>
            </div>
          ))}
          <div className="divider" />
          {quote.cgst > 0 && <Row label="CGST" value={inrExact(quote.cgst)} />}
          {quote.sgst > 0 && <Row label="SGST" value={inrExact(quote.sgst)} />}
          {quote.igst > 0 && <Row label="IGST" value={inrExact(quote.igst)} />}
          <Row label="Total" value={inrExact(quote.total)} strong />
          <span className="tiny">{quote.documentType === 'tax_invoice' ? 'A tax invoice' : 'A bill of supply'} is issued when you pay.</span>
        </div>
      )}

      <button className="btn gold block" disabled={!quote || paying} onClick={pay}>
        {paying ? 'Opening secure payment…' : quote ? <>Pay {inrExact(quote.total)} <Icon n="right" s={17} /></> : 'Pricing…'}
      </button>
    </Sheet>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="between">
      <span className={strong ? 'mid' : 'sub'}>{label}</span>
      <span className={strong ? 'num mid' : 'num sub'}>{value}</span>
    </div>
  );
}
